/* @flow */
import * as React from 'react';
import { FormattedNumber } from 'react-intl';
import { graphql } from 'react-apollo';
import idx from 'idx';
import pluralize from 'pluralize';

import { CHART_HEIGHT_SIMPLE } from 'admin/constants/Charts';
import { formatData } from 'admin/lib/Chart';
import { getAPIURL } from 'lib/apiRequest';
import ADMIN_HOME_VIEW_QUERY from 'admin/queries/AdminHomeViewQuery.gql';
import AdminActiveSchoolsTable from 'admin/components/AdminActiveSchoolsTable';
import AdminActiveTeachersTable from 'admin/components/AdminActiveTeachersTable';
import AdminHeadline from 'admin/components/AdminHeadline';
import AdminStatisticsCommunicationChart from 'admin/components/AdminStatisticsCommunicationChart';
import AdminStatisticsReachChart from 'admin/components/AdminStatisticsReachChart';
import Button from 'components/ButtonDeprecated';
import ChatMessage from 'entities/ChatMessage';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator';
import MediaObject from 'components/MediaObject';
import MessageListArea from 'chat/components/MessageListArea';
import Notice from 'components/Notice';
import NumberWithLabel from 'components/NumberWithLabel';
import OrganizationActions from 'actions/OrganizationActions';
import TabPanel from 'components/TabPanel';
import Tabs from 'components/Tabs';
import Text from 'components/Text';
import Tooltip from 'components/Tooltip';
import TooltipTrigger from 'components/TooltipTrigger';
import View from 'components/View';
import Views from 'components/Views';

import type { AdminHomeView_organizations } from 'admin/queries/__generated__/AdminHomeView';

import './styles/AdminHomeView.scss';

type Props = {
  params: {
    groupCode: string,
  },
  organization: ?AdminHomeView_organizations,
  orgId: number,
};

type State = {
  announcementsStream: any,
  // TODO: enforce a stricter schema once we know what stats we'll use long-term.
  dashboardStats: any,
};

const AdminHomeViewChartLoadingIndicator = (
  <LoadingIndicator height={CHART_HEIGHT_SIMPLE} size="small" />
);

function AdminHomeViewHeader(props: { children?: React.Node, linkTo?: string, tooltip?: string }) {
  const { children, linkTo, tooltip } = props;

  return (
    <header className="AdminHomeView-header">
      <div>
        <AdminHeadline full flush>
          {children}
          {tooltip && (
            <TooltipTrigger placement="bottom" tooltip={<Tooltip>{tooltip}</Tooltip>}>
              <Glyph
                color="graphite"
                name="help"
                size="small"
                className="AdminHomeView-header-glyph"
              />
            </TooltipTrigger>
          )}
        </AdminHeadline>
      </div>
      {linkTo && (
        <Link to={linkTo} weight="semibold" className="AdminHomeView-header-link">
          View all
        </Link>
      )}
    </header>
  );
}

// $FlowFixMe children array
function AdminHomeViewNullNotice(props: { children: string }) {
  const { children, ...others } = props;

  return (
    <Notice {...others} color="whiteout">
      {children}
    </Notice>
  );
}

class AdminHomeView extends React.Component<Props, State> {
  componentDidMount() {
    const { orgId } = this.props;
    OrganizationActions.getDashboardStats(orgId)
      .then(dashboardStats => this.setState({ dashboardStats }))
      .catch(_error => this.setState({ dashboardStats: {} }));

    OrganizationActions.getAnnouncementsStream(orgId, Number(50), Number(500))
      .then(announcementsStream =>
        this.setState({
          announcementsStream: announcementsStream.map(message => ChatMessage.fromJSON(message)),
        })
      )
      .catch(_error => this.setState({ announcementsStream: [] }));
  }

  getOrganizationType() {
    return idx(this.props.organization, _ => _.type) === 'District' ? 'district' : 'school';
  }

  getSlug() {
    return `/classes/${this.props.params.groupCode}`;
  }

  isSchool() {
    return this.getOrganizationType() === 'school';
  }

  canSeeAdminStatsV2() {
    const { organization } = this.props;
    if (organization) {
      return organization.permissions.canSeeAdminStatsV2;
    }
    return false;
  }

  // TODO: remove when admin stats v1 is deprecated
  getMessageType() {
    return this.canSeeAdminStatsV2() ? 'messages' : 'announcements';
  }

  renderHeadlineSubtitle() {
    if (this.canSeeAdminStatsV2()) {
      return (
        <Text size="medium" color="graphite">
          Encourage your community to stay engaged with these{' '}
          <Link target="_blank" href={LinkConstants.INCREASE_ENGAGEMENT}>
            helpful tips
          </Link>
          .
        </Text>
      );
    } else {
      return (
        <Text size="medium" color="graphite">
          Encourage your teachers to stay engaged with the Remind community by sending an
          announcement!
        </Text>
      );
    }
  }

  renderHeader() {
    if (!this.state || !this.state.dashboardStats) {
      return <LoadingIndicator height={120} size="small" />;
    }

    if (!this.state.dashboardStats.reach) {
      return null;
    }

    const reachByWeek = this.state.dashboardStats.reach.month.chart;
    const lastWeekIdx = reachByWeek.length - 1;
    const { parents, students, teachers } = reachByWeek[lastWeekIdx];

    return (
      <header>
        <AdminHeadline full>
          <Text weight="semibold">
            <FormattedNumber value={teachers} /> {pluralize('teacher', teachers)}
          </Text>{' '}
          sent {this.getMessageType()} to{' '}
          <Text weight="semibold">
            <FormattedNumber value={students} /> {pluralize('student', students)}
          </Text>{' '}
          and{' '}
          <Text weight="semibold">
            <FormattedNumber value={parents} /> {pluralize('parent', parents)}
          </Text>{' '}
          last week.
          <br />
          {this.renderHeadlineSubtitle()}
        </AdminHeadline>
      </header>
    );
  }

  renderActiveTeachers() {
    if (this.getOrganizationType() !== 'school') {
      return null;
    }

    const { orgId } = this.props;
    const messageType = this.getMessageType();

    if (this.canSeeAdminStatsV2()) {
      const header = (
        <AdminHomeViewHeader tooltip="Teachers who communicate the most on Remind. Messages include announcements and conversations.">
          Most communication on Remind
        </AdminHomeViewHeader>
      );
      const downloadReportButton = (
        <Button
          level="text"
          weight="semibold"
          color="mascot"
          full
          download
          href={getAPIURL(`/organizations/${orgId}/reports/teachers`)}
          eventElementName="AdminHomeView-downloadTeachersReport">
          Download full teacher report
        </Button>
      );
      return (
        <section className="AdminHomeView-activeTeachersSection">
          <div className="AdminHomeView-table">
            <Tabs tabAlign="right" buttonColor="paper" buttonSize="xsmall" type="button">
              <TabPanel title={<span>Week</span>}>
                {header}
                <AdminActiveTeachersTable
                  orgId={orgId}
                  timeWindow="week"
                  messageType={messageType}
                  actionRow={downloadReportButton}
                />
              </TabPanel>
              <TabPanel title={<span>Month</span>}>
                {header}
                <AdminActiveTeachersTable
                  orgId={orgId}
                  timeWindow="month"
                  messageType={messageType}
                  actionRow={downloadReportButton}
                />
              </TabPanel>
              <TabPanel title={<span>Year</span>}>
                {header}
                <AdminActiveTeachersTable
                  orgId={orgId}
                  timeWindow="year"
                  messageType={messageType}
                  actionRow={downloadReportButton}
                />
              </TabPanel>
            </Tabs>
          </div>
        </section>
      );
    } else {
      return (
        <section className="AdminHomeView-activeTeachersSection">
          <AdminHomeViewHeader
            to={`${this.getSlug()}/people`}
            tooltip="Teachers who use Remind the most to send announcements">
            Most active teachers in the last seven days
          </AdminHomeViewHeader>
          <AdminActiveTeachersTable orgId={orgId} messageType={messageType} />
        </section>
      );
    }
  }

  renderActiveSchools() {
    if (this.getOrganizationType() !== 'district') {
      return null;
    }

    const { orgId } = this.props;
    const messageType = this.getMessageType();

    if (this.canSeeAdminStatsV2()) {
      const header = (
        <AdminHomeViewHeader tooltip="Schools that communicate the most on Remind. Messages include announcements and conversations.">
          Most communication on Remind
        </AdminHomeViewHeader>
      );
      const downloadReportButton = (
        <Button
          level="text"
          weight="semibold"
          color="mascot"
          full
          download
          href={getAPIURL(`/organizations/${orgId}/reports/schools`)}
          eventElementName="AdminHomeView-downloadSchoolsReport">
          Download full schools report
        </Button>
      );
      return (
        <section className="AdminHomeView-activeSchoolsSection">
          <div className="AdminHomeView-table">
            <Tabs tabAlign="right" buttonColor="paper" buttonSize="xsmall" type="button">
              <TabPanel title={<span>Week</span>}>
                {header}
                <AdminActiveSchoolsTable
                  orgId={orgId}
                  timeWindow="week"
                  messageType={messageType}
                  actionRow={downloadReportButton}
                />
              </TabPanel>
              <TabPanel title={<span>Month</span>}>
                {header}
                <AdminActiveSchoolsTable
                  orgId={orgId}
                  timeWindow="month"
                  messageType={messageType}
                  actionRow={downloadReportButton}
                />
              </TabPanel>
              <TabPanel title={<span>Year</span>}>
                {header}
                <AdminActiveSchoolsTable
                  orgId={orgId}
                  timeWindow="year"
                  messageType={messageType}
                  actionRow={downloadReportButton}
                />
              </TabPanel>
            </Tabs>
          </div>
        </section>
      );
    } else {
      return (
        <section className="AdminHomeView-activeSchoolsSection">
          <AdminHomeViewHeader
            to={`${this.getSlug()}/schools`}
            tooltip="Schools that sent the most announcements">
            Most active schools in the last seven days
          </AdminHomeViewHeader>
          <AdminActiveSchoolsTable orgId={orgId} messageType={messageType} />
        </section>
      );
    }
  }

  renderChartNullState() {
    const legendHeight = 60;

    return (
      <div className="AdminHomeView-chart--null">
        <AdminHomeViewNullNotice height={CHART_HEIGHT_SIMPLE + legendHeight}>
          Sorry—no statistics for your {this.getOrganizationType()} are available at this time.
        </AdminHomeViewNullNotice>
      </div>
    );
  }
  renderActivityChart() {
    if (!this.state || !this.state.dashboardStats) {
      return AdminHomeViewChartLoadingIndicator;
    }
    if (!this.state.dashboardStats.communication) {
      return this.renderChartNullState();
    }
    const { chart } = this.state.dashboardStats.communication.week;

    return (
      <div>
        <div className="AdminHomeView-chart">
          <Link to={`${this.getSlug()}/statistics`} eventElementName="AdminHomeView-chart-activity">
            <AdminStatisticsCommunicationChart
              data={formatData(chart, 'week')}
              legend={false}
              simple
              tooltip={false}
            />
          </Link>
        </div>
      </div>
    );
  }
  renderActivity() {
    if (!this.state || !this.state.dashboardStats) {
      return null;
    }
    if (!this.state.dashboardStats.communication) {
      return this.renderChartNullState();
    }

    const { week } = this.state.dashboardStats.communication;
    const { announcements, conversations } = week.chart[week.chart.length - 1];

    return (
      <section>
        <MediaObject figure="right">
          <AdminHomeViewHeader to={`${this.getSlug()}/statistics`}>
            <NumberWithLabel label="message" value={announcements + conversations} weight="bold" />{' '}
            were delivered on Remind yesterday
          </AdminHomeViewHeader>
          <Button level="text" to={`${this.getSlug()}/statistics`} color="mascot" weight="semibold">
            View all
          </Button>
        </MediaObject>
        {this.renderActivityChart()}
      </section>
    );
  }
  renderEngagementChart() {
    if (!this.state || !this.state.dashboardStats) {
      return AdminHomeViewChartLoadingIndicator;
    }
    if (!this.state.dashboardStats.reach) {
      return this.renderChartNullState();
    }
    const { chart } = this.state.dashboardStats.reach.week;

    return (
      <div>
        <div className="AdminHomeView-chart">
          <Link
            to={`${this.getSlug()}/statistics`}
            eventElementName="AdminHomeView-chart-engagement">
            <AdminStatisticsReachChart
              data={formatData(chart, 'week')}
              legend={false}
              simple
              tooltip={false}
            />
          </Link>
        </div>
      </div>
    );
  }
  renderEngagement() {
    if (!this.state || !this.state.dashboardStats) {
      return null;
    }

    if (!this.state.dashboardStats.platform) {
      return this.renderChartNullState();
    }

    const data = this.state.dashboardStats.platform.chart.roles;
    const { parents, students, teachers } = data;

    const count =
      teachers.app +
      teachers.web +
      teachers.text +
      teachers.email +
      students.app +
      students.web +
      students.text +
      students.email +
      parents.app +
      parents.web +
      parents.text +
      parents.email;

    return (
      <section>
        <AdminHomeViewHeader
          to={`${this.getSlug()}/statistics`}
          tooltip={`
            The number of teachers, students, and parents using Remind at your
            ${' '}
            ${this.getOrganizationType()}
          `}>
          <Text weight="bold">
            <FormattedNumber value={count} /> active {pluralize('user', count)}
          </Text>{' '}
          in the last 28 days
        </AdminHomeViewHeader>
        {this.renderEngagementChart()}
      </section>
    );
  }
  renderCharts() {
    return (
      <div className="AdminHomeView-activityEngagement">
        <View>{this.renderActivity()}</View>
        {!this.canSeeAdminStatsV2() && <View>{this.renderEngagement()}</View>}
      </div>
    );
  }
  renderAnnouncementsList() {
    if (!this.state || !this.state.announcementsStream) {
      return (
        <View>
          <LoadingIndicator align="center" size="small" />
        </View>
      );
    }
    if (this.state.announcementsStream.length === 0) {
      return (
        <View>
          <AdminHomeViewNullNotice>
            Sorry—no announcements for your {this.getOrganizationType()} are available at this time.
          </AdminHomeViewNullNotice>
        </View>
      );
    }
    return (
      <MessageListArea
        disableReactionToggle
        messages={this.state.announcementsStream}
        scrollToBottom={false}
        className="AdminHomeView-announcements-list"
        scrollEventKey="announcements.admin_home.announcement_stream.scroll"
      />
    );
  }
  renderAnnouncements() {
    return (
      <section className="AdminHomeView-announcements">
        <header className="AdminHomeView-announcements-header">
          <AdminHeadline flush full>
            Recent announcements
            <TooltipTrigger
              placement="bottom"
              tooltip={
                <Tooltip>
                  A sample of announcements from publicly searchable classes in your{' '}
                  {this.getOrganizationType()}. There may be older messages that appear if no recent
                  announcements have gone out in publicly searchable classes.
                </Tooltip>
              }>
              <Glyph
                color="graphite"
                name="help"
                size="small"
                className="AdminHomeView-header-glyph"
              />
            </TooltipTrigger>
          </AdminHeadline>
        </header>
        {this.renderAnnouncementsList()}
      </section>
    );
  }
  render() {
    const { organization } = this.props;

    if (!organization) {
      return (
        <View className="AdminHomeView">
          <LoadingIndicator align="center" size="small" />
        </View>
      );
    } else if (!organization.paid) {
      const alt = 'Admin Console preview';
      const imageSrc = this.isSchool()
        ? require('assets/images/admin/AdminHomeView/placeholders/school.png')
        : require('assets/images/admin/AdminHomeView/placeholders/district.png');

      return (
        <Text align="center" element="div" className="AdminHomeView">
          <Image src={imageSrc} alt={alt} />
        </Text>
      );
    }
    return (
      <Views className="AdminHomeView">
        <View scroll className="AdminHomeView-content">
          {this.renderHeader()}
          {this.isSchool() ? this.renderActiveTeachers() : this.renderActiveSchools()}
          {this.renderCharts()}
        </View>
        <View full className="AdminHomeView-sidebar">
          {this.renderAnnouncements()}
        </View>
      </Views>
    );
  }
}
const withData = graphql(ADMIN_HOME_VIEW_QUERY, {
  options: ({ orgId }) => ({
    variables: {
      organizationId: orgId,
    },
  }),
  props: ({ data }) => ({
    organization: data.organizations ? data.organizations[0] : null,
  }),
});

export { AdminHomeView };
export default withData(AdminHomeView);
